import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute, ArmorerRoute, AdminRoute } from './components/ProtectedRoute';
import { db } from './config/firebase';
import { collection, addDoc, Timestamp, getDocs } from 'firebase/firestore';
import Login from './pages/Login';
import DiscordCallback from './pages/DiscordCallback';
import ComingSoon from './pages/ComingSoon';
import NoClearance from './pages/NoClearance';
import BehindTheCage from './pages/BehindTheCage';
import AdminPanel from './pages/AdminPanel';
import VestCommission from './pages/VestCommission';
import HelmetCommission from './pages/HelmetCommission';
import VestRevision from './pages/VestRevision';
import HelmetRevision from './pages/HelmetRevision';
import CustomCommission from './pages/CustomCommission';
import HeadwearCommission from './pages/HeadwearCommission';
import Donate from './pages/Donate';
import Metrics from './pages/Metrics';
import Updates from './pages/Updates';
import MyRequests from './pages/MyRequests';
import './App.css';

// Role Refresher Component - checks Discord roles on route changes
function RoleRefresher() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshRoles } = useAuth();
  const isFirstRender = useRef(true);
  const lastPath = useRef(location.pathname);

  useEffect(() => {
    // Only check if user is logged in
    if (!user) return;

    // Skip role check on login-related routes
    if (location.pathname === '/login' || location.pathname === '/auth/discord/callback') {
      return;
    }

    // Skip on first render (user just logged in, roles are fresh)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastPath.current = location.pathname;
      return;
    }

    // Only check if the path actually changed (not just re-renders)
    if (location.pathname === lastPath.current) {
      return;
    }
    lastPath.current = location.pathname;

    const checkRoles = async () => {
      const result = await refreshRoles();

      // If user was removed from server, lost required role, or token is invalid, redirect to login
      if (!result.success && ['not_in_server', 'token_invalid', 'missing_required_role', 'no_valid_role'].includes(result.reason)) {
        navigate('/login', { replace: true });
      }
    };

    checkRoles();
  }, [location.pathname, user, refreshRoles, navigate]);

  return null; // This component doesn't render anything
}

// Import images - use simple absolute paths
const backgroundImage = '/images/LP_Background_Photo.png';
const helmetImage = '/images/LP_Helmet_Photo.png';
const unitLogo = '/images/LP_Unit_logo.png';
const vestImage = '/images/LP_Vest_Photo.png';
const customImage = '/images/ETeam.png';
const headwearImage = '/images/LP_Hat_Photo.png';

// Modify Disclaimer Modal
function ModifyDisclaimer({ isOpen, onClose, onAcknowledge, itemType }) {
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content disclaimer-modal" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">⚠️ Modification Disclaimer</h2>
        <div className="disclaimer-text">
          <p>By clicking "I Acknowledge" you understand that only <strong>TWO items</strong> will be changed.</p>
          <p>If you need more than two items modified, you will need to submit another Modification Request.</p>
        </div>
        <div className="disclaimer-actions">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-acknowledge" onClick={onAcknowledge}>
            I Acknowledge
          </button>
        </div>
      </div>
    </div>
  );
}

// Zap Change Popup
function ZapChangePopup({ isOpen, onClose, user, userData, onSuccess }) {
  const [discordName, setDiscordName] = useState('');
  const [currentZap, setCurrentZap] = useState('');
  const [updatedZap, setUpdatedZap] = useState('');
  const [colorOption, setColorOption] = useState('MC');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!discordName || !currentZap || !updatedZap) {
      alert('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'requests'), {
        userId: user.uid,
        userEmail: userData?.email || '',
        username: userData?.username || userData?.serverNickname || '',
        type: 'ZAP',
        status: 'Unassigned',
        discordName,
        currentZap,
        updatedZap,
        colorOption,
        createdAt: Timestamp.now()
      });
      
      onSuccess();
      onClose();
      // Reset form
      setDiscordName('');
      setCurrentZap('');
      setUpdatedZap('');
      setColorOption('MC');
    } catch (error) {
      console.error('Error submitting ZAP change:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content zap-modal" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">⚡ ZAP Change Request</h2>
        <form onSubmit={handleSubmit}>
          <div className="modal-field">
            <label>CSQN Discord Name</label>
            <input 
              type="text"
              value={discordName}
              onChange={(e) => setDiscordName(e.target.value)}
              placeholder="J. Smith"
              required
            />
          </div>
          <div className="modal-field">
            <label>Current ZAP</label>
            <input 
              type="text"
              value={currentZap}
              onChange={(e) => setCurrentZap(e.target.value)}
              placeholder="Current ZAP number"
              required
            />
          </div>
          <div className="modal-field">
            <label>Updated ZAP</label>
            <input 
              type="text"
              value={updatedZap}
              onChange={(e) => setUpdatedZap(e.target.value)}
              placeholder="New ZAP number"
              required
            />
          </div>
          <div className="modal-field">
            <label>MC, Black, or White on MCB</label>
            <select 
              value={colorOption}
              onChange={(e) => setColorOption(e.target.value)}
            >
              <option value="MC">MC</option>
              <option value="Black">Black</option>
              <option value="White on MCB">White on MCB</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-acknowledge" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Home Page Component
function HomePage() {
  const { user, userData, isArmorer, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [buildDropdownOpen, setBuildDropdownOpen] = useState(false);
  const [buildSubMenu, setBuildSubMenu] = useState(null); // 'vest', 'helmet', or null
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerType, setDisclaimerType] = useState('');
  const [showZapPopup, setShowZapPopup] = useState(false);
  const [zapSuccess, setZapSuccess] = useState(false);
  
  // Notification badge counts
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [pendingDonationsCount, setPendingDonationsCount] = useState(0);
  
  const buildDropdownRef = useRef(null);
  const cageDropdownRef = useRef(null);
  const adminDropdownRef = useRef(null);
  const [cageDropdownOpen, setCageDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  // Fetch notification counts for armorers and admins
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      if (isArmorer || isAdmin) {
        try {
          // Get unassigned requests count
          const requestsSnap = await getDocs(collection(db, 'requests'));
          const unassigned = requestsSnap.docs.filter(doc => {
            const data = doc.data();
            return data.status === 'Unassigned';
          }).length;
          setUnassignedCount(unassigned);
        } catch (error) {
          console.error('Error fetching unassigned count:', error);
        }
      }
      
      if (isAdmin) {
        try {
          // Get pending approval users count
          const usersSnap = await getDocs(collection(db, 'users'));
          const pending = usersSnap.docs.filter(doc => {
            const data = doc.data();
            return !data.approved;
          }).length;
          setPendingApprovalCount(pending);
          
          // Get pending donations count
          const donationsSnap = await getDocs(collection(db, 'donations'));
          const pendingDonations = donationsSnap.docs.filter(doc => {
            const data = doc.data();
            return !data.status || data.status === 'Pending';
          }).length;
          setPendingDonationsCount(pendingDonations);
        } catch (error) {
          console.error('Error fetching pending approval count:', error);
        }
      }
    };
    
    fetchNotificationCounts();
  }, [isArmorer, isAdmin]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (buildDropdownRef.current && !buildDropdownRef.current.contains(event.target)) {
        setBuildDropdownOpen(false);
        setBuildSubMenu(null);
      }
      if (cageDropdownRef.current && !cageDropdownRef.current.contains(event.target)) {
        setCageDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setAdminDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmailClick = () => {
    window.location.href = 'mailto:contact@creature.supply?subject=Creature Supply Inquiry';
  };

  const handleModifyClick = (type) => {
    setDisclaimerType(type);
    setShowDisclaimer(true);
  };

  const handleAcknowledge = () => {
    setShowDisclaimer(false);
    // Navigate to appropriate revision page
    if (disclaimerType === 'Vest') {
      navigate('/commission/vest-revision');
    } else {
      navigate('/commission/helmet-revision');
    }
  };

  return (
    <div className="app">
      {/* Background */}
      <div className="background-container">
        <img src={backgroundImage} alt="" className="background-image" />
        <div className="background-overlay"></div>
      </div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-left">
          <Link to="/" className="nav-brand">
            <img src={unitLogo} alt="Creature Supply Logo" className="unit-logo" />
            <h1 className="site-title">Creature Supply</h1>
          </Link>
          <Link to="/donate" className="donate-nav-btn">Donate</Link>
        </div>

        {/* Mobile menu button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></span>
        </button>

        <div className={`nav-right ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link to="/" className="nav-link">Home</Link>
          
          {/* Build Requests Dropdown - Consolidated */}
          <div className="dropdown build-dropdown" ref={buildDropdownRef}>
            <button 
              className="nav-link dropdown-trigger"
              onClick={() => {
                setBuildDropdownOpen(!buildDropdownOpen);
                setBuildSubMenu(null);
                setCageDropdownOpen(false);
                setAdminDropdownOpen(false);
              }}
            >
              Build Requests
              <svg className={`dropdown-arrow ${buildDropdownOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div className={`dropdown-menu build-menu ${buildDropdownOpen ? 'open' : ''}`}>
              {/* Vest & Belts */}
              <div 
                className={`dropdown-item has-submenu ${buildSubMenu === 'vest' ? 'submenu-open' : ''}`}
                onMouseEnter={() => setBuildSubMenu('vest')}
              >
                Vest & Belts
                <svg className="submenu-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 6 15 12 9 18"></polyline>
                </svg>
                <div className={`submenu ${buildSubMenu === 'vest' ? 'open' : ''}`}>
                  <Link to="/commission/vest" className="dropdown-item" onClick={() => setBuildDropdownOpen(false)}>
                    New Commission <span className="price-tag">$30</span>
                  </Link>
                  <button onClick={() => { handleModifyClick('Vest'); setBuildDropdownOpen(false); }} className="dropdown-item dropdown-btn">
                    Modify Existing <span className="price-tag">$15</span>
                  </button>
                </div>
              </div>
              
              {/* Helmet */}
              <div 
                className={`dropdown-item has-submenu ${buildSubMenu === 'helmet' ? 'submenu-open' : ''}`}
                onMouseEnter={() => setBuildSubMenu('helmet')}
              >
                Helmet
                <svg className="submenu-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 6 15 12 9 18"></polyline>
                </svg>
                <div className={`submenu ${buildSubMenu === 'helmet' ? 'open' : ''}`}>
                  <Link to="/commission/helmet" className="dropdown-item" onClick={() => setBuildDropdownOpen(false)}>
                    New Commission <span className="price-tag">$30</span>
                  </Link>
                  <button onClick={() => { handleModifyClick('Helmet'); setBuildDropdownOpen(false); }} className="dropdown-item dropdown-btn">
                    Modify Existing <span className="price-tag">$15</span>
                  </button>
                </div>
              </div>
              
              {/* T-Shirt, Hoodie or Patch */}
              <Link 
                to="/commission/custom" 
                className="dropdown-item"
                onClick={() => setBuildDropdownOpen(false)}
                onMouseEnter={() => setBuildSubMenu(null)}
              >
                T-Shirt, Hoodie or Patch <span className="price-tag">$25</span>
              </Link>
              
              {/* Custom Headwear */}
              <Link 
                to="/commission/headwear" 
                className="dropdown-item"
                onClick={() => setBuildDropdownOpen(false)}
                onMouseEnter={() => setBuildSubMenu(null)}
              >
                Custom Headwear <span className="price-tag">$20</span>
              </Link>
            </div>
          </div>

          {/* View Updates - Everyone */}
          <Link to="/updates" className="nav-link updates-link">
            Updates
          </Link>

          {/* My Requests - All logged in users */}
          <Link to="/my-requests" className="nav-link">
            My Requests <span className="beta-tag">(BETA)</span>
          </Link>

          {/* Behind the Cage - Armorer Only (View Requests only, no Metrics) */}
          {isArmorer && !isAdmin && (
            <Link to="/requests" className="nav-link cage-link nav-with-badge">
              Behind the Cage
              {unassignedCount > 0 && <span className="nav-badge">{unassignedCount}</span>}
            </Link>
          )}

          {/* Behind the Cage Dropdown - Admin gets full dropdown */}
          {isAdmin && (
            <div className="dropdown" ref={cageDropdownRef}>
              <button 
                className="nav-link dropdown-trigger cage-link nav-with-badge"
                onClick={() => {
                  setCageDropdownOpen(!cageDropdownOpen);
                  setBuildDropdownOpen(false);
                  setAdminDropdownOpen(false);
                }}
              >
                Behind the Cage
                {unassignedCount > 0 && <span className="nav-badge">{unassignedCount}</span>}
                <svg className={`dropdown-arrow ${cageDropdownOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className={`dropdown-menu ${cageDropdownOpen ? 'open' : ''}`}>
                <Link to="/requests" className="dropdown-item">
                  View Requests
                </Link>
                <Link to="/metrics" className="dropdown-item">
                  Metrics
                </Link>
              </div>
            </div>
          )}

          {/* Admin Dropdown - Admin Only */}
          {isAdmin && (
            <div className="dropdown" ref={adminDropdownRef}>
              <button 
                className="nav-link dropdown-trigger admin-link nav-with-badge"
                onClick={() => {
                  setAdminDropdownOpen(!adminDropdownOpen);
                  setBuildDropdownOpen(false);
                  setCageDropdownOpen(false);
                }}
              >
                Admin
                <span className="badge-group">
                  {pendingApprovalCount > 0 && <span className="nav-badge admin-badge-count">{pendingApprovalCount}</span>}
                  {pendingDonationsCount > 0 && <span className="nav-badge donation-badge-count">{pendingDonationsCount}</span>}
                </span>
                <svg className={`dropdown-arrow ${adminDropdownOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className={`dropdown-menu ${adminDropdownOpen ? 'open' : ''}`}>
                <Link to="/admin" className="dropdown-item">
                  User Management
                </Link>
                <Link to="/admin?tab=donations" className="dropdown-item">
                  Donation Requests
                </Link>
              </div>
            </div>
          )}

          {/* User Info & Logout */}
          {user && (
            <div className="user-section">
              <span className="user-name">{userData?.serverNickname || userData?.username || user.email}</span>
              <span className={`role-badge ${isAdmin ? 'admin' : isArmorer ? 'armorer' : 'user'}`}>
                {isAdmin ? 'ADMIN' : isArmorer ? 'ARMORER' : 'USER'}
              </span>
              <button className="logout-btn" onClick={logout}>Logout</button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="main-content">
        {/* ZAP Change Button - Centered above cards */}
        <div className="zap-center-container">
          <button 
            className="zap-center-btn"
            onClick={() => setShowZapPopup(true)}
          >
            ⚡ Zap Change
          </button>
        </div>


        <div className="cards-container cards-2x2">
          {/* Vest & Belts Card */}
          <div className="gear-card">
            <div className="card-image-container">
              <img src={vestImage} alt="Vest & Belts" className="card-image" />
            </div>
            <div className="card-content">
              <h3 className="card-title">Vest & Belts</h3>
              <p className="card-description">
                Custom plate carrier configurations with your choice of pouches, 
                belts, holsters, and accessories.
              </p>
              <div className="card-actions">
                <Link to="/commission/vest" className="btn btn-primary">
                  New Commission <span className="btn-price">$30</span>
                </Link>
                <button onClick={() => handleModifyClick('Vest')} className="btn btn-secondary">
                  Modify Existing <span className="btn-price">$15</span>
                </button>
              </div>
            </div>
          </div>

          {/* Helmet Card */}
          <div className="gear-card">
            <div className="card-image-container">
              <img src={helmetImage} alt="Helmet" className="card-image" />
            </div>
            <div className="card-content">
              <h3 className="card-title">Helmet</h3>
              <p className="card-description">
                Custom helmet setups with NVG mounts, comms, counterweights, 
                and protective accessories.
              </p>
              <div className="card-actions">
                <Link to="/commission/helmet" className="btn btn-primary">
                  New Commission <span className="btn-price">$30</span>
                </Link>
                <button onClick={() => handleModifyClick('Helmet')} className="btn btn-secondary">
                  Modify Existing <span className="btn-price">$15</span>
                </button>
              </div>
            </div>
          </div>

          {/* T-Shirt, Hoodie, or Patch Card */}
          <div className="gear-card">
            <div className="card-image-container">
              <img src={customImage} alt="T-Shirt, Hoodie, or Patch" className="card-image" />
            </div>
            <div className="card-content">
              <h3 className="card-title">T-Shirt, Hoodie or Patch</h3>
              <p className="card-description">
                Custom apparel and patches with your design specifications.
              </p>
              <div className="card-actions">
                <Link to="/commission/custom" className="btn btn-primary">
                  New Commission <span className="btn-price">$25 per item</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Custom Headwear Card */}
          <div className="gear-card">
            <div className="card-image-container">
              <img src={headwearImage} alt="Custom Headwear" className="card-image" />
            </div>
            <div className="card-content">
              <h3 className="card-title">Custom Headwear</h3>
              <p className="card-description">
                Baseball caps, beanies, boonie hats, and more with custom designs.
              </p>
              <div className="card-actions">
                <Link to="/commission/headwear" className="btn btn-primary">
                  New Commission <span className="btn-price">$20 per item</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Floating Contact Button */}
      <button className="floating-contact-btn" onClick={handleEmailClick}>
        <svg className="email-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
          <polyline points="22,6 12,13 2,6"></polyline>
        </svg>
        Contact
      </button>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2026 Creature Supply. For authorized personnel only.</p>
      </footer>


      {/* Modify Disclaimer Modal */}
      <ModifyDisclaimer 
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
        onAcknowledge={handleAcknowledge}
        itemType={disclaimerType}
      />

      {/* Zap Change Popup */}
      <ZapChangePopup
        isOpen={showZapPopup}
        onClose={() => setShowZapPopup(false)}
        user={user}
        userData={userData}
        onSuccess={() => {
          setZapSuccess(true);
          setTimeout(() => setZapSuccess(false), 3000);
        }}
      />

      {/* Zap Success Toast */}
      {zapSuccess && (
        <div className="toast-success">
          ✓ ZAP Change request submitted successfully!
        </div>
      )}
    </div>
  );
}

// Main App with Router and Auth
function App() {
  return (
    <AuthProvider>
      <Router>
        <RoleRefresher />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/discord/callback" element={<DiscordCallback />} />
          <Route path="/no-clearance" element={<NoClearance />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coming-soon" 
            element={
              <ProtectedRoute>
                <ComingSoon />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/behind-the-cage" 
            element={
              <ArmorerRoute>
                <BehindTheCage />
              </ArmorerRoute>
            } 
          />
          <Route 
            path="/requests" 
            element={
              <ArmorerRoute>
                <BehindTheCage />
              </ArmorerRoute>
            } 
          />
          <Route 
            path="/metrics" 
            element={
              <AdminRoute>
                <Metrics />
              </AdminRoute>
            } 
          />
          <Route 
            path="/updates" 
            element={<Updates />} 
          />
          <Route 
            path="/commission/vest" 
            element={
              <ProtectedRoute>
                <VestCommission />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commission/helmet" 
            element={
              <ProtectedRoute>
                <HelmetCommission />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commission/vest-revision" 
            element={
              <ProtectedRoute>
                <VestRevision />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commission/helmet-revision" 
            element={
              <ProtectedRoute>
                <HelmetRevision />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commission/custom" 
            element={
              <ProtectedRoute>
                <CustomCommission />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commission/headwear" 
            element={
              <ProtectedRoute>
                <HeadwearCommission />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/donate" 
            element={
              <ProtectedRoute>
                <Donate />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } 
          />
          <Route 
            path="/my-requests" 
            element={
              <ProtectedRoute>
                <MyRequests />
              </ProtectedRoute>
            } 
          />
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
